import { FC, useEffect, useMemo, useState } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';

import { AdminOrderBrandTable } from './AdminOrderBrandTable';
import { AdminOrderListModal } from './AdminOrderListModal';
import {
  OrderStatDrillDown,
  OrderStatKind,
  OrderStatOrder,
} from './adminOrderStat.types';
import {
  aggregateOrderStat,
  drillDownOrders,
  orderBrandKeyOf,
  orderBrandNameOf,
} from './adminOrderStat.util';
import { exportOrderStatExcel } from './adminOrderStatExcel';
import { AdminPenetrationSection } from './AdminPenetrationSection';
import { AnalyticsCalendar } from './AnalyticsCalendar';
import { AnalyticsDailyTrend } from './AnalyticsDailyTrend';
import {
  AnalyticsDateFilter,
  DateRange,
  defaultRange,
} from './AnalyticsDateFilter';
import { CenterLoading, EmptyBox, ErrorBox, NoticeBox, StatCard } from './AnalyticsShared';
import {
  ORDER_FETCH_LIMIT,
  ORDER_MAX_RANGE_DAYS,
  useAdminOrderStat,
} from './useAdminOrderStat';

//* 화환·답례품 탭 본체. 두 탭은 지표 이름과 날짜 축만 다르고 구조가 같아 한 컴포넌트로 쓴다.

interface KindConfig {
  /** 기간 필터 라벨 = 날짜 축 */
  dateLabel: string;
  trendTitle: string;
  calendarHint: string;
  /** 집계 기준 각주 */
  notice: string;
}

const CONFIG: Record<OrderStatKind, KindConfig> = {
  flower: {
    dateLabel: '판매일',
    trendTitle: '판매일별 화환 결제액',
    calendarHint: '날짜를 누르면 그날 판매된 화환 주문이 열립니다.',
    notice:
      '결제완료·확정 주문만 집계합니다(취소 건은 빠집니다). 금액은 실결제액 기준이며, 레거시 답례품 주문은 제외합니다. 날짜 축은 결제일입니다.',
  },
  gift: {
    dateLabel: '주문일',
    trendTitle: '주문일별 답례품 순매출',
    calendarHint: '날짜를 누르면 그날 들어온 답례품 주문이 열립니다.',
    notice:
      '순매출은 실결제액에서 취소액을 뺀 값입니다(발송 후 환불은 차감하지 않습니다 — 수익 대시보드와 같은 규칙). 날짜 축은 주문 생성일입니다.',
  },
};

const VIEW_ITEMS = [
  { key: 'table' as const, label: '표', title: '상조별 매출 표와 일별 추이' },
  { key: 'calendar' as const, label: '달력', title: '날짜별 매출 히트맵' },
  {
    key: 'penetration' as const,
    label: '구매율',
    title: '발인일 기준 — 발인한 상가 중 몇 %가 샀는지',
  },
];

export const AdminOrderAnalyticsTab: FC<{ kind: OrderStatKind }> =
  function AdminOrderAnalyticsTab({ kind }) {
    const config = CONFIG[kind];

    const [range, setRange] = useState<DateRange>(defaultRange);
    const [brandKeys, setBrandKeys] = useState<string[]>([]);
    //* penetration = 발인일 축(구매율). 나머지 둘은 판매일/주문일 축이다
    const [view, setView] = useState<'table' | 'calendar' | 'penetration'>('table');
    //* 구매율 뷰는 상가+주문을 따로 부른다 — 열어보기 전엔 조회하지 않고,
    //* 한 번 연 뒤에는 숨겨만 둬서 뷰를 오갈 때마다 다시 부르지 않는다
    const [penetrationOpened, setPenetrationOpened] = useState(false);
    const [drill, setDrill] = useState<{
      drill: OrderStatDrillDown;
      title: string;
    } | null>(null);

    const {
      status,
      orders,
      truncated,
      errorMessage,
      resolveBrandName,
      refetch,
    } = useAdminOrderStat(
      kind,
      range.from,
      range.to,
    );

    //* 상조 칩 — 상조 필터와 무관하게 고정돼야 하므로 원자료에서 뽑는다
    const brandOptions = useMemo(() => {
      const counts = new Map<string, { key: string; name: string; count: number }>();
      orders.forEach((order: OrderStatOrder) => {
        const key = orderBrandKeyOf(order);
        const entry = counts.get(key);
        if (entry) entry.count += 1;
        else
          counts.set(key, {
            key,
            name: orderBrandNameOf(order, resolveBrandName),
            count: 1,
          });
      });
      return Array.from(counts.values()).sort(
        (a, b) => b.count - a.count || a.name.localeCompare(b.name),
      );
    }, [orders, resolveBrandName]);

    //* 기간을 옮겨 선택한 상조의 주문이 없어지면 칩도 사라진다 —
    //* 보이지 않는 필터가 남아 "주문 없음"으로 보이지 않도록 함께 해제한다
    useEffect(() => {
      if (orders.length === 0) return;
      setBrandKeys((prev) => {
        const available = new Set(brandOptions.map((option) => option.key));
        const next = prev.filter((key) => available.has(key));
        return next.length === prev.length ? prev : next;
      });
    }, [brandOptions, orders.length]);

    const result = useMemo(
      () => aggregateOrderStat(orders, kind, { brandKeys }, range, resolveBrandName),
      [orders, kind, brandKeys, range, resolveBrandName],
    );

    //* 금액을 주 지표로 두고 건수를 보조로 — 매출 흐름이 한눈에 보이게
    const dayPoints = useMemo(
      () => result.days.map((day) => ({ date: day.date, value: Math.round(day.amount) })),
      [result.days],
    );
    const dayLabels = useMemo(() => ({ value: '금액', unit: '원' }), []);

    const drillOrders = useMemo(
      () => (drill ? drillDownOrders(orders, kind, { brandKeys }, drill.drill) : []),
      [drill, orders, kind, brandKeys],
    );

    const toggleBrand = (key: string) => {
      setBrandKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
      );
    };

    const totals = result.totals;
    const hasData = totals.orderCount > 0 || totals.canceledCount > 0;

    return (
      <div className="space-y-3">
        {/* 필터 */}
        <div className="space-y-2.5 rounded-lg bg-white p-3 shadow-smd">
          <AnalyticsDateFilter
            label={config.dateLabel}
            range={range}
            onChange={setRange}
            maxRangeDays={ORDER_MAX_RANGE_DAYS}
          />

          {brandOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="w-11 shrink-0 text-13 font-medium text-gray-600">
                상조
              </span>
              <button
                type="button"
                onClick={() => setBrandKeys([])}
                className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
                  brandKeys.length === 0
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                전체
              </button>
              {brandOptions.map((brand) => (
                <button
                  key={brand.key}
                  type="button"
                  onClick={() => toggleBrand(brand.key)}
                  className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
                    brandKeys.includes(brand.key)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {brand.name}
                  <span className="ml-1 tabular-nums opacity-70">{brand.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {status === 'loading' && <CenterLoading />}
        {status === 'error' && (
          <ErrorBox message={errorMessage ?? '오류가 발생했습니다.'} onRetry={refetch} />
        )}

        {status === 'success' && (
          <>
            {truncated && (
              <NoticeBox tone="warn">
                조회 상한 {ORDER_FETCH_LIMIT.toLocaleString()}건에 걸려 일부만
                집계했습니다. 기간을 좁혀주세요.
              </NoticeBox>
            )}

            <div className="flex items-center gap-1.5">
              {VIEW_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setView(item.key);
                    if (item.key === 'penetration') setPenetrationOpened(true);
                  }}
                  title={item.title}
                  className={`rounded-lg px-4 py-1.5 text-14 font-medium ${
                    view === item.key
                      ? 'bg-gray-800 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              {view !== 'penetration' && hasData && (
                <button
                  type="button"
                  onClick={() => setDrill({ drill: {}, title: '전체 주문' })}
                  className="ml-auto rounded-lg bg-white px-3 py-1.5 text-14 font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  전체 목록 보기
                </button>
              )}
            </div>

            {/* 발인일 축이라 위쪽 요약(판매일 축)과 숫자가 다르다 — 섹션이 자기 요약을 따로 낸다 */}
            {penetrationOpened && (
              <div className={view === 'penetration' ? undefined : 'hidden'}>
                <AdminPenetrationSection
                  kind={kind}
                  range={range}
                  brandKeys={brandKeys}
                />
              </div>
            )}

            {view === 'penetration' ? null : !hasData ? (
              <EmptyBox message="해당 조건의 주문이 없습니다." />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <StatCard
                    label={kind === 'gift' ? '순매출' : '결제액'}
                    value={`${formatPrice(Math.round(totals.amount))}원`}
                    sub={`건당 ${
                      totals.orderCount > 0
                        ? formatPrice(Math.round(totals.amount / totals.orderCount))
                        : '-'
                    }원`}
                    accent="indigo"
                  />
                  <StatCard
                    label="주문"
                    value={totals.orderCount.toLocaleString()}
                    sub={kind === 'gift' ? `수량 ${totals.quantity.toLocaleString()}` : undefined}
                    accent="emerald"
                  />
                  <StatCard
                    label="상가"
                    value={totals.feventCount.toLocaleString()}
                    sub={`상가당 ${
                      totals.feventCount > 0
                        ? (totals.orderCount / totals.feventCount).toFixed(1)
                        : '-'
                    }건`}
                  />
                  {kind === 'gift' ? (
                    <StatCard
                      label="전체취소"
                      value={totals.canceledCount.toLocaleString()}
                      sub="결제 전액 취소된 주문"
                      accent="red"
                    />
                  ) : (
                    <StatCard
                      label="상조"
                      value={result.brands.length.toLocaleString()}
                      sub="주문이 있는 상조 수"
                    />
                  )}
                </div>

                <NoticeBox>{config.notice}</NoticeBox>

                {view === 'table' ? (
                  <>
                    <AnalyticsDailyTrend
                      title={config.trendTitle}
                      days={dayPoints}
                      labels={dayLabels}
                    />
                    <AdminOrderBrandTable
                      kind={kind}
                      result={result}
                      onExport={() => exportOrderStatExcel(kind, result, range)}
                      onBrandClick={(brandKey, brandName) =>
                        setDrill({ drill: { brandKey }, title: brandName })
                      }
                    />
                  </>
                ) : (
                  <AnalyticsCalendar
                    days={dayPoints}
                    labels={dayLabels}
                    hint={config.calendarHint}
                    onDayClick={(date) =>
                      setDrill({ drill: { date }, title: `${date} ${config.dateLabel}` })
                    }
                    onWeekClick={(from, to, label) =>
                      setDrill({ drill: { dateFrom: from, dateTo: to }, title: label })
                    }
                  />
                )}
              </>
            )}
          </>
        )}

        {drill && (
          <AdminOrderListModal
            kind={kind}
            title={drill.title}
            subtitle={`${range.from} ~ ${range.to}`}
            orders={drillOrders}
            onClose={() => setDrill(null)}
          />
        )}
      </div>
    );
  };
